import { Component } from "../base/Component";
import { List } from "../components/list/List";
import { Alignment, ModuleLogger, getLogger, DOMUtil, setClassIncluded, getThemeForKey, getThemeColor } from "@mcleod/core";
import { ListProps } from "../components/list/ListProps";
import { OnScreen } from "./OnScreen";
import { Anchor, AnchorMutation, AnchorProps } from "./Anchor";
import { ComponentUtil } from "../base/ComponentUtil";
import { Label, ScreenStack, SelectionListener, StringOrPropsOrComponent } from "..";
import { OverlayStyles } from "./OverlayStyles";
import { OverlayProps } from "./OverlayProps";
import { KeyHandlerGroup } from "../events/KeyHandlerGroup";
import { Panel } from "../components/panel/Panel";
import { McLeodMainPageUtil } from "../base/McLeodMainPageUtil";

export interface OverlayedList extends List {
  _overlay?: Overlay;
}

export interface DropdownProps extends OverlayProps {
  align: Alignment;
  position: Alignment;
}

export class Overlay {
  private _component: Component;
  private _overlayDiv: HTMLDivElement;
  private _overlayContent: Panel;
  private _props: Partial<OverlayProps>;
  private static _log: ModuleLogger;

  constructor(component: Component, props?: Partial<OverlayProps>) {
    this._component = component;
    this._props = props;
    this._overlayDiv = document.createElement("div");
    this._applyOverlayDivCSS(props);
    if (props?.closeOnClickOff !== false) {
      let closeOverlayFunction: (event: Event) => void;
      let clickInProgress = false;
      if (props?.closeHandler == null)
        closeOverlayFunction = (event) => Overlay.hideOverlay(this, this._props?.componentToFocusOnClose);
      else
        closeOverlayFunction = (event) => props.closeHandler();

      //ensure that both starting a click (onmousedown) and completing a click (onmouseup) has to be outside the component
      //before we use that click to close the overlay
      this._overlayDiv.onmousedown = (event) => {
        if (event.target === this._overlayDiv)
          clickInProgress = true;
      };
      this._component._element.onmouseenter = (event) => {
        clickInProgress = false;
      };
      this._overlayDiv.onmouseup = (event) => {
        try {
          if (clickInProgress === true)
          closeOverlayFunction(event);
        }
        finally {
          clickInProgress = false;
        }
      };
    }
    this._overlayContent = new Panel();
    this._overlayContent.className = OverlayStyles.overlayContent;
    this._overlayContent.addClickListener(event => event.stopPropagation());
    this._overlayContent._element.style.cursor = null;
    this._overlayContent.add(this._component);
    this._overlayDiv.appendChild(this._overlayContent._element);

    this._overlayDiv.style.display = "block";
    this._overlayDiv.style.backgroundColor = props?.greyedBackground === true ? 
      getThemeColor(getThemeForKey("component.palette.overlay.backgroundColor")) : "";
    if (props?.centered === true)
      setClassIncluded(component, OverlayStyles.centered);
    if (props?.width != null)
      this._overlayContent.style.width = DOMUtil.getSizeSpecifier(props.width);
    if (props?.height != null)
      this._overlayContent.style.height = DOMUtil.getSizeSpecifier(props.height);
    if (props?.style != null) {
      for (const key in props.style) {
        this._overlayContent.style[key] = props.style[key];
      }
    }
  }

  private _applyOverlayDivCSS(props: Partial<OverlayProps>) {
    if (props?.coverPageHeader !== false)
      this._overlayDiv.className = OverlayStyles.overlay;
    else {
      this._overlayDiv.className = OverlayStyles.overlayBelowPageHeader;
      //have to override the top/height styles here...
      //can't calculate height of page header when OverlayStyles is created...the page header has no height at that time
      const pageHeaderHeight = McLeodMainPageUtil.getPageHeaderHeight();
      this._overlayDiv.style.top = pageHeaderHeight + "px";
      this._overlayDiv.style.height = "calc(100% - " + pageHeaderHeight + "px)";
    }
  }

  get component(): Component {
    return this._component;
  }

  get overlayDiv(): HTMLDivElement {
    return this._overlayDiv;
  }

  fillKeyHandlerGroup(recreate: boolean = false) {
    this._overlayContent.fillKeyHandlerGroup(recreate);
  }

  getKeyHandlerGroup(): KeyHandlerGroup {
    return this._overlayContent.getKeyHandlerGroup();
  }

  getOverlayContent(): Panel {
    return this._overlayContent;
  }

  get overlayContentFirstElement(): HTMLElement {
    //this._overlayContent._element.firstChild is (maybe) the best guess we can make when the component is not known
    //but in most cases the component should be known, and we should just use the component's element
    return this.component?._element || (this._overlayContent._element.firstChild as HTMLElement);
  }

  get zIndex(): number {
    return DOMUtil.convertStyleAttrToNumber(this.overlayDiv.style.zIndex);
  }

  set zIndex(value: number) {
    this._overlayDiv.style.zIndex = value.toString();
  }

  alignAndObserve() {
    const overlayContentElement = this.overlayContentFirstElement;
    let compRect = ComponentUtil.getRect(overlayContentElement);
    if (this._props?.anchor != null) {
      compRect = Anchor.alignRect(compRect, this._props.anchor);
      let mutation = Overlay.ensureOnScreen(overlayContentElement, this._props?.anchor, this._component);
      const obs = new MutationObserver(() => {
        mutation = Overlay.ensureOnScreen(overlayContentElement, mutation.newAnchor, this._component);
        this._props?.anchor?.onMutate?.(overlayContentElement, mutation);
      });
      obs.observe(this._overlayContent._element, { childList: true, subtree: true });
    }
    // overlayContent.addEventListener("resize", () => Overlay.ensureOnScreen(this._overlayContent) );
  }

  remove(componentToFocusOnClose?: Component) {
    ScreenStack.popOverlay(this);
    document.body.removeChild(this._overlayDiv);
    //give the component that the mouse was over before the overlay was displayed enough time to consume (and ignore)
    //its mouseenter event.  then null out that component so that future mouseenter events work.
    setTimeout(() => Component.setPreOverlayMouseOverComponent(null), 500);
    if (this._props != null && this._props.onClose != null)
      this._props.onClose();
    if (componentToFocusOnClose != null)
      componentToFocusOnClose.focus();
  }

  /**
   * This function displays a component over the top of the rest of the page.  Another div completely covers the screen so that the user can't interact with
   * any of the existing page's components.
   *
   * @param component The component that will be the overlay.
   * @param props OverlayProps
   * @returns Returns a HTMLDivElement that can be passed to hideOverlay to make sure the proper overlay is removed.
   */
  public static showInOverlay(component: Component, props?: Partial<OverlayProps>): Overlay {
    const overlay = new Overlay(component, props);
    overlay.zIndex = ScreenStack.getNewHighestZIndex();
    overlay.fillKeyHandlerGroup();
    ScreenStack.pushOverlay(overlay);
    document.body.appendChild(overlay.overlayDiv);
    overlay.alignAndObserve();
    return overlay;
  }

  /**
   * Hides alls overlays.  If you are trying to hide just one overlay, use Overlay.hideOverlay(Overlay, boolean?).
   *
   * @returns void
   */
 public static hideAllOverlays() {
  for (const overlay of ScreenStack.getAllOverlays()) {
      overlay.remove();
  }
}

  /**
   * Hides a previously shown overlay.  The showOverlay function returns a value that can be passed to the overlayToClose argument.
   * This will ensure the correct overlay is closed in the case that there are multiple overlays on top of each other.
   *
   * @param overlayToClose the Overlay that you wish to close.
   * @param componentToFocusOnClose When provided, a Component that should get focus when the overlay is closed.
   */
  public static hideOverlay(overlayToClose: Overlay, componentToFocusOnClose?: Component) {
    if (ScreenStack.overlayInStack(overlayToClose) === true)
      overlayToClose.remove(componentToFocusOnClose);
  }

  /**
   * A dropdown is just a special case of an overlay that shows a list with the specified items.
   *
   * @param anchorComponent
   * @param items
   * @param onSelect
   * @param listProps
   * @param dropdownProps
   * @param selectedItem
   * @returns
   */
  public static showDropdown(anchorComponent: HTMLElement | Component, items: any, onSelect?: SelectionListener, listProps?: Partial<ListProps>, dropdownProps?: Partial<DropdownProps>, selectedItem?: StringOrPropsOrComponent): OverlayedList {
    if (typeof items === "function")
      items = items();
    const list: OverlayedList = new List({
      className: OverlayStyles.popup,
      items: items,
      selectedItem: selectedItem,
      selectOnEnter: true,
      ...listProps
    });
    list.addSelectionListener(event => {
      if (onSelect != null)
        onSelect(event);
      if (!event.defaultPrevented)
        Overlay.hideOverlay(overlayContent, overlayContent._props.componentToFocusOnClose);
    });
    list.setOnClickForAllItems(_event => Overlay.hideOverlay(overlayContent, overlayContent._props.componentToFocusOnClose));
    if (listProps == null || listProps.maxHeight === undefined)
      list.maxHeight = 220;
    const anchor = dropdownProps?.anchor || {
      anchor: anchorComponent,
      align: dropdownProps?.align,
      position: dropdownProps?.position,
    };
    const comp = items == null || items.length === 0 ? 
      new Label({ className: OverlayStyles.popup, caption: "No items", padding: 8, color: "subtle", allowSelect: false, ...listProps }) : list;
    Anchor.sizeToAnchor(comp, anchor);
    const overlayContent = Overlay.showInOverlay(comp, { anchor: anchor, ...dropdownProps });
    list._overlay = overlayContent;
    list.focus();
    if (selectedItem == null && list.items.length > 0)
      list.selectedIndex = 0;
    return list;
  }

  public static findOverlay(component: Component): Overlay {
    for (const overlay of ScreenStack.getAllOverlays()) {
      if (overlay.component === component)
        return overlay;
    }
    return null;
  }

  /**
   * This sets the absolute position of 'component' relative to 'anchor.'  It will also adjust the relevant sizing of component to match that of anchor.  For example,
   * if position = Alignment.BOTTOM or position = Alignment.TOP, component's width will be set to the same width as anchor.  If this is not the desired behavior,
   * component needs to have an explicit width (or null if you want to component maintain its width:auto)
   *
   * @deprecated Use Anchor instead
   * @param component The Component that you are trying to position
   * @param anchor The Component to which you are trying to position 'component' relative to
   * @param alignment Specifies which side of the anchor we should align the dropdown with.  For example, align=Alignment.RIGHT would specify that the right side of component
   *   will be aligned to the right side of anchor (that would typically only be used with position: TOP or position.BOTTOM)
   * @param position Specifes the position of the dropdown relative to the anchor.  For example, position=Alignment.BOTTOM specifies that the component should be
   *   positioned under the anchor.
   */
  public static alignToAnchor(component: Component, anchor: Component | Element, alignment: Alignment = Alignment.LEFT, position: Alignment = Alignment.BOTTOM) {
    component._element.classList.add(OverlayStyles.popup);
    const anchorElement: Element = anchor instanceof Component ? anchor._element : anchor;
    const anchorRect = anchorElement.getBoundingClientRect == null ? anchorElement.getBoundingClientRect() : anchorElement.getBoundingClientRect();
    if (position === Alignment.TOP) {
      if (component.width === undefined)
        component.width = anchorRect.width;
      else if (typeof component.width === "number")
        component.top = anchorRect.top - component.width;
      component.left = anchorRect.left;
    } else if (position === Alignment.RIGHT) {
      component.top = anchorRect.top;
      component.left = anchorRect.right;
      component.height = anchorRect.height;
      if (component.borderTopRightRadius === undefined && component.borderBottomLeftRadius === undefined && component.borderRadius === undefined) {
        component.borderTopRightRadius = 8;
        component.borderBottomRightRadius = 8;
      }
    } else if (position === Alignment.LEFT) {
      component.top = anchorRect.top;
      if (typeof component.width === "number")
        component.left = anchorRect.left - component.width;
      component.height = anchorRect.height;
    }
    else {
      component.top = anchorRect.bottom;
      if (component.width === undefined)
        component.width = anchorRect.width;
      if (alignment === Alignment.LEFT)
        component.left = anchorRect.left;
      else if (typeof component.width === "number")
        component.left = anchorRect.right - component.width;
      else if (component.width === null)
        component.right = document.body.clientWidth - anchorRect.right;
      if (component.borderBottomRightRadius === undefined && component.borderBottomLeftRadius === undefined && component.borderRadius === undefined) {
        component.borderBottomRightRadius = 4;
        component.borderBottomLeftRadius = 4;
      }
    }
  }

  public static ensureOnScreen(elem: HTMLElement, anchor: AnchorProps, component: Component): AnchorMutation {
    const minWidth = anchor?.minWidth || DOMUtil.convertSizeStyleToPixels(component.minWidth, document.body.offsetWidth);
    const minHeight = anchor?.minHeight || DOMUtil.convertSizeStyleToPixels(component.minHeight, document.body.offsetHeight);
    return OnScreen.ensureOnScreen(elem, { ...anchor, minWidth: minWidth, minHeight: minHeight });
  }

  public static getPopupBorderRadiusProps(anchor: AnchorProps): Partial<CSSStyleDeclaration> {
    const pos = anchor?.position || Alignment.BOTTOM;
    const props = { borderTopLeftRadius: "", borderTopRightRadius: "", borderBottomLeftRadius: "", borderBottomRightRadius: "" };
    if (pos === Alignment.RIGHT)
      return { ...props, borderTopRightRadius: "4px", borderBottomRightRadius: "4px" };
    else if (pos === Alignment.TOP)
      return { ...props, borderTopLeftRadius: "4px", borderTopRightRadius: "4px" };
    else if (pos === Alignment.LEFT)
      return { ...props, borderTopLeftRadius: "4px", borderTopRightRadius: "4px" };
    else
      return { ...props, borderBottomLeftRadius: "4px", borderBottomRightRadius: "4px" };
  }

  private static get log() {
    if (this._log == null)
      this._log = getLogger("components.page.Overlay");
    return this._log;
  }
}
